/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { Condominium, PersonProfile, Section, Unit } from '../../../../../entities';
import { ProfileForm, schema } from '../../../../../validations/profile.validation';
import { Autocomplete, Button, ScrollableTabPanel } from '../../../../../components';
import ProfileRole from '../../../../../entities/profile-role';
import { lazyFetchSections, lazyFetchUnitsWithSection, UnitsWithSection, usePersons } from '../../../../../hooks';
import { useAuthContext } from '../../../../../contexts/AuthContext';

const roleNameMapper: Record<ProfileRole, string> = {
  [ProfileRole.Employee]: 'Funcionário',
  [ProfileRole.Unknown]: 'Morador',
  [ProfileRole.Tenant]: 'Inquilino',
  [ProfileRole.Dweller]: 'Morador',
  [ProfileRole.Guest]: 'Visitante',
  [ProfileRole.Owner]: 'Proprietário',
  [ProfileRole.Manager]: 'Manager',
};

interface ProfilePanelProps {
  visible: boolean;
  profile: PersonProfile;
  condominiums: Condominium[];
  personId?: string;
  inProgress: boolean;
  onClickClose: () => void;
  onSubmit: (value: PersonProfile) => void;
  onClickDelete: (value: PersonProfile) => void;
}

export const ProfilePanel = ({
  visible,
  profile,
  condominiums,
  personId,
  inProgress,
  onClickClose,
  onSubmit,
  onClickDelete,
}: ProfilePanelProps): JSX.Element => {
  const {
    reset,
    handleSubmit,
    getValues,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ProfileForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });
  const [units, setUnits] = useState<UnitsWithSection[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  const role = useWatch({ control, name: 'role' });
  const condominiumId = useWatch({ control, name: 'condominiumId' });
  const sectionId = useWatch({ control, name: 'sectionId' });
  const unitId = useWatch({ control, name: 'unitId' });

  const { user } = useAuthContext();

  const handleLoadUnits = useCallback(async () => {
    if (!condominiumId) return;
    try {
      const response = await lazyFetchUnitsWithSection(condominiumId);
      response.sort((a, b) => {
        const unitA = Number(a.number);
        const unitB = Number(b.number);
        if (unitA < unitB) return -1;
        if (unitA > unitB) return 1;
        return 0;
      });
      setUnits(response);
    } catch (err) {
      enqueueSnackbar('Não foi possível carregar as unidades', { variant: 'error' });
    }
  }, [condominiumId, enqueueSnackbar]);

  const handleLoadSections = useCallback(async () => {
    if (!condominiumId) return;
    try {
      const response = await lazyFetchSections(condominiumId);
      response.sort();

      setSections(response);
    } catch (error: unknown) {
      enqueueSnackbar('Não foi possível carregar os blocos', { variant: 'error' });
    }
  }, [condominiumId, enqueueSnackbar]);

  const handleSelectedSection = useCallback(async () => {
    const selectedUnit = units.find(({ id }) => unitId === id);
    if (selectedUnit?.section) {
      setValue('sectionId', selectedUnit.section!.id!);
    }
  }, [setValue, unitId, units]);

  useEffect(() => {
    if (!sectionId) {
      handleSelectedSection();
    }
  }, [sectionId, handleSelectedSection]);

  useEffect(() => {
    handleLoadUnits();
    handleLoadSections();
  }, [condominiumId, handleLoadUnits, handleLoadSections]);

  useEffect(() => {
    reset({
      role: profile.role ?? undefined,
      condominiumId: profile.condominiumId ?? undefined,
      unitId: profile.unitId ?? undefined,
    });
  }, [profile, reset]);

  const isAdministrativeRole = useMemo(() => role === ProfileRole.Manager || role === ProfileRole.Employee, [role]);

  return (
    <ScrollableTabPanel
      visible={visible}
      title="Dados do perfil"
      onClickSubmit={handleSubmit(() => onSubmit({ ...profile, ...getValues() }))}
      onClickClose={onClickClose}
      loading={isSubmitting}
    >
      <Autocomplete
        label="Função"
        value={(role as ProfileRole) ?? null}
        placeholder="Selecione a função"
        options={[ProfileRole.Manager, ProfileRole.Employee]}
        getOptionLabel={(opt) => roleNameMapper[opt] ?? ''}
        error={Boolean(errors.role)}
        helperText={errors.role?.message ?? ' '}
        onChange={(e, data) => {
          if (data === ProfileRole.Manager || data === ProfileRole.Employee) {
            setValue('condominiumId', undefined);
          }

          setValue('role', data as ProfileRole);
        }}
      />

      <Autocomplete
        label="Condomínio"
        disabled={role === ProfileRole.Manager}
        value={condominiums.find(({ id }) => id === condominiumId) ?? null}
        placeholder="Selecione a condomínio"
        options={condominiums}
        helperText={errors.condominiumId?.message ?? ' '}
        error={Boolean(errors.condominiumId)}
        getOptionLabel={(opt) => opt.name}
        onChange={(e, data) => {
          setValue('unitId', null);
          setValue('sectionId', null);
          setValue('condominiumId', data?.id ?? null);
        }}
      />

      {sections.length === 0 || !condominiumId ? (
        <></>
      ) : (
        <Autocomplete
          label="Bloco"
          disabled={role !== ProfileRole.Dweller || !condominiumId || sections.length === 0}
          value={sections.find(({ id }) => id === sectionId) ?? null}
          placeholder="Selecione um bloco"
          options={sections}
          helperText={errors.sectionId?.message ?? ' '}
          error={Boolean(errors.sectionId)}
          getOptionLabel={(opt) => opt.label}
          onChange={(e, data) => {
            setValue('sectionId', data?.id);
            setValue('unitId', null);
          }}
        />
      )}

      <Autocomplete
        label="Unidade"
        disabled={!condominiumId || !role || isAdministrativeRole || (!sectionId && sections.length > 0)}
        value={units.find(({ id }) => unitId === id) ?? null}
        placeholder="Selecione a unidade"
        renderOption={(props, opt) => (
          <li {...props} key={opt.id}>
            {opt.number} {opt.disabled ? ' - DESATIVADA' : ''}
          </li>
        )}
        options={sectionId ? units.filter((unit) => unit.section?.id === sectionId) : units}
        helperText={errors.unitId?.message ?? ' '}
        error={Boolean(errors.unitId)}
        getOptionLabel={(opt) => opt.label}
        onChange={(e, data) => setValue('unitId', data?.id)}
      />

      {!profile.id || (personId === user?.uid && role === ProfileRole.Manager) || role === ProfileRole.Dweller ? (
        <></>
      ) : (
        <Button
          variant="text"
          color="primary"
          onClick={() => onClickDelete(profile)}
          mustConfirm
          mustConfirmContent="Deseja excluir o perfil do usuário?"
          disabled={inProgress}
          loading={inProgress}
        >
          Excluir
        </Button>
      )}
    </ScrollableTabPanel>
  );
};

export default ProfilePanel;
